import Loader from "../components/Loader";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import {
  fetchSupplierData,
  fetchSupplierBoatsData,
  toggleSupplierStatus,
} from "../redux/actions/staffActions";
import SubHeader from "../components/SubHeader";
import { getFileName, getFileType, handleView, pipViewDate } from "../auth/Pip";
import ReactPagination from "../layout/ReactPagination";
import PaginationDropdown from "../layout/PaginationDropdown";
import StatusCol from "../components/StatusCol";
import DeleteModal from "../components/modals/DeleteModal";
import CustomDropdown from "../components/CustomDropdown";
// Replace the broken "import " line with:
import SupplierPerformanceChart from "../components/Charts/SupplierPerformanceChart";

const normalizeStatus = (value) =>
  `${value ?? ""}`
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

const getActivityStatus = (item = {}) =>
  [
    item?.status,
    item?.boat_status,
    item?.task_status,
    item?.job_status,
    item?.activity_status,
    item?.punctuality_status,
    item?.performance_status,
    item?.statusUI?.label,
    item?.status_ui?.label,
  ]
    .map((value) => normalizeStatus(value))
    .find(Boolean) || "";

const formatStatusLabel = (value) => {
  const normalizedValue = normalizeStatus(value);

  if (!normalizedValue) {
    return "-";
  }

  return normalizedValue
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");
};

const supplierCompletionOptions = ["COMPLETE", "INCOMPLETE"];

const getCompletionStatus = (value) => {
  const normalizedValue = normalizeStatus(value);

  if (!normalizedValue) {
    return "";
  }

  if (
    [
      "COMPLETED",
      "COMPLETE",
      "DONE",
      "CLOSED",
    ].includes(normalizedValue)
  ) {
    return "COMPLETE";
  }

  return "INCOMPLETE";
};


const SupplierDetail = () => {
  const [showAll, setShowAll] = useState(false);
  const [activityStatus, setActivityStatus] = useState("");
  const [jobCurrentPage, setJobCurrentPage] = useState(0);
  const [jobRowsPerPage, setJobRowsPerPage] = useState(5);
  const [boatCurrentPage, setBoatCurrentPage] = useState(0);
  const [boatRowsPerPage, setBoatRowsPerPage] = useState(5);
  const { isLoading1, supplierData, supplier_boats_data } = useSelector(
    (state) => state?.staffReducer,
  );

  const data = useLocation()?.state?.data || {};
  const supplierTabs = supplierData?.tabs || {};
  const supInfo =
    supplierTabs?.supplier_information?.account_information ||
    supplierData?.supplier_information ||
    {};
  const supplierInfoTab = supplierTabs?.supplier_information || {};
  const supJobs =
    supplierTabs?.supplier_jobs?.summary_cards || supplierData?.supplier_jobs || {};
  const supplierJobHistory =
    supplierTabs?.supplier_jobs?.job_history ||
    supplierData?.supplier_jobs?.job_history ||
    [];
  const supplierActivity =
    supplierTabs?.activity_tracker ||
    supplierData?.supplier_activity ||
    supplierData?.activity ||
    {};
  const liveStatus =
    supplierActivity?.live_status || supplierActivity?.liveStatus || {};
  const perfSummary =
    supplierActivity?.performance_summary ||
    supplierActivity?.perfSummary ||
    {};
  const supEngagement =
    supplierActivity?.supplier_engagement ||
    supplierActivity?.engagement ||
    {};
  const workActivity =
    supplierActivity?.work_activity || supplierActivity?.workActivity || {};
  const supplierBoats = supplier_boats_data?.boats || [];
  const getMatchingSupplierJobs = (boat = {}) =>
    supplierJobHistory.filter((job) => {
      const boatRego = `${boat?.rego ?? boat?.boat_rego ?? ""}`.trim().toLowerCase();
      const jobRego = `${job?.rego ?? job?.boatReg ?? job?.boat_rego ?? ""}`.trim().toLowerCase();
      const boatName = `${boat?.boat_name ?? ""}`.trim().toLowerCase();
      const jobBoatName = `${job?.boatName ?? job?.boat_name ?? ""}`.trim().toLowerCase();

      return (
        (boatRego && jobRego && boatRego === jobRego) ||
        (boatName && jobBoatName && boatName === jobBoatName)
      );
    });

  const getSupplierBoatStatuses = (boat = {}) =>
    Array.from(
      new Set(
        [
          getCompletionStatus(getActivityStatus(boat)),
          ...getMatchingSupplierJobs(boat).map((job) =>
            getCompletionStatus(getActivityStatus(job)),
          ),
        ].filter(Boolean),
      ),
    );

  const getSupplierBoatDisplayStatus = (boat = {}) =>
    getSupplierBoatStatuses(boat)[0] || "";

  const supplierBoatStatusOptions = [
    { label: "All Status", value: "" },
    ...Array.from(
      new Set(
        [
          ...supplierCompletionOptions,
          ...supplierBoats.flatMap((boat) => getSupplierBoatStatuses(boat)),
          ...supplierJobHistory
            .map((job) => getCompletionStatus(getActivityStatus(job)))
            .filter(Boolean),
        ],
      ),
    ).map((status) => ({
      label: formatStatusLabel(status),
      value: status,
    })),
  ];

  const filteredSupplierBoats = supplierBoats?.filter((boat) =>
    activityStatus ? getSupplierBoatStatuses(boat).includes(activityStatus) : true,
  );
  const paginatedSupplierBoats =
    filteredSupplierBoats?.slice(
      boatCurrentPage * boatRowsPerPage,
      (boatCurrentPage + 1) * boatRowsPerPage,
    ) || [];

  const performanceSummaryRows = [
    {
      label: "Total Jobs",
      value: perfSummary?.total_jobs ?? 0,
      icon: (
        <svg width={20} height={20} viewBox="0 0 18 20" fill="none">
          <path
            d="M2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4C0 3.45 0.195833 2.97917 0.5875 2.5875C0.979167 2.19583 1.45 2 2 2H6.2C6.41667 1.4 6.77917 0.916667 7.2875 0.55C7.79583 0.183333 8.36667 0 9 0C9.63333 0 10.2042 0.183333 10.7125 0.55C11.2208 0.916667 11.5833 1.4 11.8 2H16C16.55 2 17.0208 2.19583 17.4125 2.5875C17.8042 2.97917 18 3.45 18 4V18C18 18.55 17.8042 19.0208 17.4125 19.4125C17.0208 19.8042 16.55 20 16 20H2ZM2 18H16V4H2V18ZM4 16H11V14H4V16ZM4 12H14V10H4V12ZM4 8H14V6H4V8Z"
            fill="#013760"
          />
        </svg>
      ),
    },
    {
      label: "Completed Jobs",
      value: perfSummary?.completed_jobs ?? 0,
      icon: (
        <svg width="22" height="21" viewBox="0 0 22 21" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7.6 21L5.7 17.8L2.1 17L2.45 13.3L0 10.5L2.45 7.7L2.1 4L5.7 3.2L7.6 0L11 1.45L14.4 0L16.3 3.2L19.9 4L19.55 7.7L22 10.5L19.55 13.3L19.9 17L16.3 17.8L14.4 21L11 19.55L7.6 21ZM8.45 18.45L11 17.35L13.6 18.45L15 16.05L17.75 15.4L17.5 12.6L19.35 10.5L17.5 8.35L17.75 5.55L15 4.95L13.55 2.55L11 3.65L8.4 2.55L7 4.95L4.25 5.55L4.5 8.35L2.65 10.5L4.5 12.6L4.25 15.45L7 16.05L8.45 18.45ZM9.95 14.05L15.6 8.4L14.2 6.95L9.95 11.2L7.8 9.1L6.4 10.5L9.95 14.05Z" fill="#009C35" />
        </svg>

      ),
    },
    {
      label: "Pending Jobs",
      value: perfSummary?.pending_jobs ?? 0,
      icon: (
        <svg width={20} height={20} viewBox="0 0 24 24" fill="none">
          <path
            d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm0-2a8 8 0 1 0 0-16 8 8 0 0 0 0 16zm1-8h4v2h-6V7h2v5z"
            fill="#EF9F27"
          />
        </svg>
      ),
    },
    {
      label: "In Progress",
      value: perfSummary?.in_progress_jobs ?? 0,
      icon: (
        <svg width="19" height="21" viewBox="0 0 19 21" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 21C12.6167 21 11.4375 20.5125 10.4625 19.5375C9.4875 18.5625 9 17.3833 9 16C9 14.6167 9.4875 13.4375 10.4625 12.4625C11.4375 11.4875 12.6167 11 14 11C15.3833 11 16.5625 11.4875 17.5375 12.4625C18.5125 13.4375 19 14.6167 19 16C19 17.3833 18.5125 18.5625 17.5375 19.5375C16.5625 20.5125 15.3833 21 14 21ZM15.675 18.375L16.375 17.675L14.5 15.8V13H13.5V16.2L15.675 18.375ZM2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4C0 3.45 0.195833 2.97917 0.5875 2.5875C0.979167 2.19583 1.45 2 2 2H6.175C6.35833 1.41667 6.71667 0.9375 7.25 0.5625C7.78333 0.1875 8.36667 0 9 0C9.66667 0 10.2625 0.1875 10.7875 0.5625C11.3125 0.9375 11.6667 1.41667 11.85 2H16C16.55 2 17.0208 2.19583 17.4125 2.5875C17.8042 2.97917 18 3.45 18 4V10.25C17.7 10.0333 17.3833 9.85 17.05 9.7C16.7167 9.55 16.3667 9.41667 16 9.3V4H14V7H4V4H2V18H7.3C7.41667 18.3667 7.55 18.7167 7.7 19.05C7.85 19.3833 8.03333 19.7 8.25 20H2ZM9 4C9.28333 4 9.52083 3.90417 9.7125 3.7125C9.90417 3.52083 10 3.28333 10 3C10 2.71667 9.90417 2.47917 9.7125 2.2875C9.52083 2.09583 9.28333 2 9 2C8.71667 2 8.47917 2.09583 8.2875 2.2875C8.09583 2.47917 8 2.71667 8 3C8 3.28333 8.09583 3.52083 8.2875 3.7125C8.47917 3.90417 8.71667 4 9 4Z" fill="#596575" />
        </svg>

      ),
    },
    {
      label: "Overdue Jobs",
      value: perfSummary?.overdue_jobs ?? 0,
      icon: (
        <svg width="23" height="23" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g clip-path="url(#clip0_2143_1472)">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M7.1875 1.4375C7.47344 1.4375 7.74766 1.55109 7.94985 1.75328C8.15204 1.95546 8.26562 2.22969 8.26562 2.51562V4.3125H15.4531V2.51562C15.4531 2.22969 15.5667 1.95546 15.7689 1.75328C15.9711 1.55109 16.2453 1.4375 16.5312 1.4375C16.8172 1.4375 17.0914 1.55109 17.2936 1.75328C17.4958 1.95546 17.6094 2.22969 17.6094 2.51562V4.3125H20.125C20.5062 4.3125 20.8719 4.46395 21.1415 4.73353C21.411 5.00312 21.5625 5.36875 21.5625 5.75V11.8594C21.5625 12.1453 21.4489 12.4195 21.2467 12.6217C21.0445 12.8239 20.7703 12.9375 20.4844 12.9375C20.1984 12.9375 19.9242 12.8239 19.722 12.6217C19.5198 12.4195 19.4062 12.1453 19.4062 11.8594V10.7812H3.59375V19.4062H11.8594C12.1453 19.4062 12.4195 19.5198 12.6217 19.722C12.8239 19.9242 12.9375 20.1984 12.9375 20.4844C12.9375 20.7703 12.8239 21.0445 12.6217 21.2467C12.4195 21.4489 12.1453 21.5625 11.8594 21.5625H2.875C2.49375 21.5625 2.12812 21.411 1.85853 21.1415C1.58895 20.8719 1.4375 20.5062 1.4375 20.125V5.75C1.4375 5.36875 1.58895 5.00312 1.85853 4.73353C2.12812 4.46395 2.49375 4.3125 2.875 4.3125H6.10938V2.51562C6.10938 2.22969 6.22296 1.95546 6.42515 1.75328C6.62734 1.55109 6.90156 1.4375 7.1875 1.4375ZM3.59375 8.625H19.4062V6.46875H3.59375V8.625ZM16.215 14.6913C16.1163 14.5853 15.9973 14.5004 15.865 14.4414C15.7328 14.3825 15.59 14.3508 15.4453 14.3483C15.3005 14.3457 15.1567 14.3723 15.0225 14.4266C14.8882 14.4808 14.7663 14.5615 14.6639 14.6639C14.5615 14.7663 14.4808 14.8882 14.4266 15.0225C14.3723 15.1567 14.3457 15.3005 14.3483 15.4453C14.3508 15.59 14.3825 15.7328 14.4414 15.865C14.5004 15.9973 14.5853 16.1163 14.6913 16.215L17.1637 18.6875L14.6913 21.16C14.5853 21.2587 14.5004 21.3777 14.4414 21.51C14.3825 21.6422 14.3508 21.785 14.3483 21.9297C14.3457 22.0745 14.3723 22.2183 14.4266 22.3525C14.4808 22.4868 14.5615 22.6087 14.6639 22.7111C14.7663 22.8135 14.8882 22.8942 15.0225 22.9484C15.1567 23.0027 15.3005 23.0293 15.4453 23.0267C15.59 23.0242 15.7328 22.9925 15.865 22.9336C15.9973 22.8746 16.1163 22.7897 16.215 22.6838L18.6875 20.2113L21.16 22.6838C21.2587 22.7897 21.3777 22.8746 21.51 22.9336C21.6422 22.9925 21.785 23.0242 21.9297 23.0267C22.0745 23.0293 22.2183 23.0027 22.3525 22.9484C22.4868 22.8942 22.6087 22.8135 22.7111 22.7111C22.8135 22.6087 22.8942 22.4868 22.9484 22.3525C23.0027 22.2183 23.0293 22.0745 23.0267 21.9297C23.0242 21.785 22.9925 21.6422 22.9336 21.51C22.8746 21.3777 22.7897 21.2587 22.6838 21.16L20.2113 18.6875L22.6838 16.215C22.7897 16.1163 22.8746 15.9973 22.9336 15.865C22.9925 15.7328 23.0242 15.59 23.0267 15.4453C23.0293 15.3005 23.0027 15.1567 22.9484 15.0225C22.8942 14.8882 22.8135 14.7663 22.7111 14.6639C22.6087 14.5615 22.4868 14.4808 22.3525 14.4266C22.2183 14.3723 22.0745 14.3457 21.9297 14.3483C21.785 14.3508 21.6422 14.3825 21.51 14.4414C21.3777 14.5004 21.2587 14.5853 21.16 14.6913L18.6875 17.1637L16.215 14.6913Z" fill="#CB0B03" />
          </g>
          <defs>
            <clipPath id="clip0_2143_1472">
              <rect width="23" height="23" fill="white" />
            </clipPath>
          </defs>
        </svg>

      ),
    },
    {
      label: "Completion Rate",
      value: `${perfSummary?.completion_rate ?? 0}%`,
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M19 5L5 19M17.5 20C19.167 20 20 19.143 20 17C20 14.857 19.167 14 17.5 14C15.833 14 15 14.857 15 17C15 19.143 15.833 20 17.5 20ZM6.5 10C8.167 10 9 9.143 9 7C9 4.857 8.167 4 6.5 4C4.833 4 4 4.857 4 7C4 9.143 4.833 10 6.5 10Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
        </svg>

      ),
    },
  ];

  const engagementRows = [
    { label: "Accepted Offers", value: supEngagement?.accepted_offers ?? 0 },
    { label: "Rejected Offers", value: supEngagement?.rejected_offers ?? 0 },
    {
      label: "Response Rate",
      value: `${supEngagement?.response_rate ?? 0}%`,
      description: "Offer acceptance rate",
    },
  ];

  const workActivityRows = [
    { label: "Total Services", value: workActivity?.total_services ?? 0 },
    {
      label: "Photos Uploaded",
      value: workActivity?.total_uploaded_photos ?? 0,
    },
    {
      label: "Materials Used",
      value: workActivity?.total_materials_used ?? 0,
    },
    {
      label: "Total Working Hours",
      value: workActivity?.total_working_hours ?? 0,
      description: "Across all jobs",
    },
    {
      label: "Avg Job Time",
      value: `${workActivity?.average_job_time_hours ?? 0} hrs`,
      description: "Per job average",
    },
  ];

  const renderActivityCards = (title, rows) => (
    <div className="ct_white_bg p-4 ct_border_radius_12 ct_mb_32">
      <h3 className="ct_fs_18 ct_fw_600 mb-4 ct_ff_ProximaNova_semibold">
        {title}
      </h3>
      <div className="row">
        {rows.map((row, index) => (
          <div
            className="col-xxl-4 col-xl-6 col-lg-4 col-md-4 mb-4 mb-xxl-0"
            key={`${title}-${index}`}
          >
            <div className="ct_white_bg ct_p_24 ct_user_card_new ct_border_radius_12 h-100">
              <div>
                <p className="mb-1 ct_fs_14 ct_text_para ct_ff_ProximaNova_semibold">
                  {row.label}
                </p>
                <h3 className="ct_fs_30 mb-0 text-dark ct_ff_ProximaNova_bold">
                  {row.value}
                </h3>
                {row.description && (
                  <p className="mb-0 mt-2 ct_fs_13 ct_text_para">
                    {row.description}
                  </p>
                )}
              </div>
              {row.icon && (
                <div
                  className="d-flex align-items-center justify-content-center"
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: "rgba(1,55,96,0.06)",
                    flexShrink: 0,
                  }}
                >
                  {row.icon}
                </div>
              )}

            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderActivityTable = (title, rows) => (
    <div className="ct_white_bg p-4 ct_border_radius_12 ct_mb_32">
      <h3 className="ct_fs_18 ct_fw_600 mb-4 ct_ff_ProximaNova_semibold">
        {title}
      </h3>
      <div className="custom_table_wrapper">
        <div className="table-responsive ct_custom_table">
          <table className="table">
            <thead>
              <tr>
                <th>METRIC</th>
                <th>VALUE</th>
                <th>DETAILS</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={`${title}-${index}`}>
                  <td className="ct_text_theme ct_ff_ProximaNova_bold">
                    {row.label}
                  </td>
                  <td className="ct_text_para">{row.value}</td>
                  <td className="ct_text_para">{row.description || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSupplierBoatsTable = () => (
    <>
      <div className="ct_white_bg p-4 ct_border_radius_12 ct_mb_32">
        <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-4">
          <h3 className="ct_fs_18 ct_fw_600 mb-0 ct_ff_ProximaNova_semibold">
            Supplier Boats Activity
          </h3>
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <div className="ct_min_w_220">
              <CustomDropdown
                options={supplierBoatStatusOptions}
                selectedValue={activityStatus}
                onChange={(value) => setActivityStatus(value)}
                placeholder="Select Status"
              />
            </div>
          </div>
        </div>
        <div className="custom_table_wrapper">
          <div className="table-responsive ct_custom_table">
            <table className="table">
              <thead>
                <tr>
                  <th>S.NO</th>
                  <th>BOAT NAME</th>
                  <th>REGO</th>
                  <th>STATUS</th>
                  {/* <th>OWNER NAME</th>
                  <th>TOTAL JOBS</th>
                  <th>COMPLETED</th>
                  <th>PENDING</th>
                  <th>SERVICES</th>
                  <th>PHOTOS</th>
                  <th>INVOICES</th>
                  <th>JOB SHEETS</th>
                  <th>LATEST JOB DATE</th> */}
                </tr>
              </thead>
              <tbody>
                {paginatedSupplierBoats?.length ? (
                  paginatedSupplierBoats.map((item, index) => (
                    <tr key={item?.boat_id || index}>
                      <td className="ct_text_para">
                        {boatCurrentPage * boatRowsPerPage + index + 1}
                      </td>
                      <td className="ct_text_theme ct_ff_ProximaNova_bold">
                        {item?.boat_name || "-"}
                      </td>
                      <td className="ct_text_para">{item?.rego || "-"}</td>
                      <StatusCol
                        status={getSupplierBoatDisplayStatus(item)}
                        type={"jobHis"}
                      />
                      {/* <td className="ct_text_para">{item?.owner_name || "-"}</td>
                      <td className="ct_text_para">{item?.total_jobs ?? 0}</td>
                      <td className="ct_text_para">{item?.completed_jobs ?? 0}</td>
                      <td className="ct_text_para">{item?.pending_jobs ?? 0}</td>
                      <td className="ct_text_para">{item?.total_services ?? 0}</td>
                      <td className="ct_text_para">{item?.total_photos ?? 0}</td>
                      <td className="ct_text_para">{item?.total_invoices ?? 0}</td>
                      <td className="ct_text_para">{item?.total_job_sheets ?? 0}</td>
                      <td className="ct_text_para">
                        {pipViewDate(item?.latest_job_date) || "-"}
                      </td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={4}>
                      <h3 className="mt-3 mb-0 ct_fs_18 text-center">
                        No supplier boats found
                      </h3>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {filteredSupplierBoats?.length > 0 && (
        <div className="d-flex align-items-center flex-wrap justify-content-between gap-3 mt-3 mb-3">
          <PaginationDropdown
            onChange={(val) => {
              setBoatRowsPerPage(Number(val));
              setBoatCurrentPage(0);
            }}
          />
          <ReactPagination
            pageCount={Math.ceil(filteredSupplierBoats.length / boatRowsPerPage)}
            onPageChange={(data) => setBoatCurrentPage(data.selected)}
            currentPage={boatCurrentPage}
          />
        </div>
      )}
    </>
  );

  const MAX_VISIBLE = 5;

  const supplierCompanyDetails =
    supplierInfoTab?.company_details || supplierData?.supplier_information?.company_details;
  const services =
    supplierInfoTab?.services_offered ||
    supplierData?.supplier_information?.services_offered_list ||
    [];
  const visibleServices = showAll ? services : services.slice(0, MAX_VISIBLE);
  const remainingCount = services.length - MAX_VISIBLE;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isToggle, setIsToggle] = useState(false);
  const supplierStatus = Number(
    supplierData?.status ?? 0,
  );
  const isSupplierActive = supplierStatus === 1;
  const statusButtonText = isSupplierActive ? "Block" : "Activate";
  const statusModalHeading = isSupplierActive
    ? "Block Supplier"
    : "Activate Supplier";
  const statusModalText = isSupplierActive
    ? "Are you sure you want to block this supplier?"
    : "Are you sure you want to activate this supplier?";

  const displayUsers =
    supplierJobHistory?.slice(
      jobCurrentPage * jobRowsPerPage,
      (jobCurrentPage + 1) * jobRowsPerPage,
    ) || [];

  const handlePageClick = (data) => {
    setJobCurrentPage(data.selected);
  };

  useEffect(() => {
    dispatch(fetchSupplierData(data?.id));
    dispatch(fetchSupplierBoatsData(data?.id));
  }, []);

  useEffect(() => {
    setBoatCurrentPage(0);
  }, [activityStatus]);

  useEffect(() => {
    const totalPages = Math.ceil((supplierJobHistory?.length || 0) / jobRowsPerPage);

    if (totalPages > 0 && jobCurrentPage >= totalPages) {
      setJobCurrentPage(totalPages - 1);
    }

    if (totalPages === 0 && jobCurrentPage !== 0) {
      setJobCurrentPage(0);
    }
  }, [supplierJobHistory?.length, jobRowsPerPage, jobCurrentPage]);

  useEffect(() => {
    const totalPages = Math.ceil(
      (filteredSupplierBoats?.length || 0) / boatRowsPerPage,
    );

    if (totalPages > 0 && boatCurrentPage >= totalPages) {
      setBoatCurrentPage(totalPages - 1);
    }

    if (totalPages === 0 && boatCurrentPage !== 0) {
      setBoatCurrentPage(0);
    }
  }, [filteredSupplierBoats?.length, boatRowsPerPage, boatCurrentPage]);

  const handleStatusToggle = () => {
    const callback = (response) => {
      if (response?.success) {
        dispatch(fetchSupplierData(data?.id));
      }
    };

    dispatch(
      toggleSupplierStatus({
        payload: data?.id,
        callback,
      }),
    );
  };

  const onHandleClick = () => {
    setIsToggle(!isToggle);
  };

  if (isLoading1) {
    return <Loader />;
  }
  return (
    <div className="ct_dashbaord_bg">
      <div className={`ct_dashbaord_main ${isToggle == false && "ct_active"}`}>
        <Sidebar path="" />
        <div className="ct_content_right">
          <Header onClick={onHandleClick} />

          <div class="ct_px_23 ct_mt_30 pb-5">
            <div class="">
              <div className="d-flex justify-content-between gap-3 ct_mb_30 ct_flex_col_575">
                <SubHeader/>
                <div className="ct_wrap_100_1">
                  <button
                    className={`ct_wrap_100_1 ${isSupplierActive
                        ? "ct_custom_block_btn"
                        : "ct_custom_btm ct_border_radius_0"
                      }`}
                    data-bs-toggle="modal"
                    data-bs-target="#supplier_status_modal"
                  >
                    <svg
                      className="me-2"
                      width="22"
                      height="22"
                      viewBox="0 0 22 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M10.9997 20.1663C16.0624 20.1663 20.1663 16.0624 20.1663 10.9997C20.1663 5.93692 16.0624 1.83301 10.9997 1.83301C5.93692 1.83301 1.83301 5.93692 1.83301 10.9997C1.83301 16.0624 5.93692 20.1663 10.9997 20.1663ZM6.50251 16.793L16.793 6.50251C17.8898 7.91341 18.4337 9.67628 18.3224 11.4599C18.2112 13.2435 17.4524 14.9251 16.1888 16.1888C14.9251 17.4524 13.2435 18.2112 11.4599 18.3224C9.67628 18.4337 7.91341 17.8898 6.50251 16.793ZM5.20634 15.4968C4.10952 14.0859 3.56565 12.3231 3.67692 10.5395C3.78818 8.75584 4.54691 7.07423 5.81057 5.81057C7.07423 4.54691 8.75584 3.78818 10.5395 3.67692C12.3231 3.56565 14.0859 4.10952 15.4968 5.20634L5.20634 15.4968Z"
                        fill="white"
                      />
                    </svg>
                    {statusButtonText}
                  </button>
                </div>
              </div>

              <div className="row">
                <div className="col-xxl-4 col-xl-4 mb-4 mb-xl-0">
                  <div className="ct_white_bg ct_border_radius_25 ct_user_dtl_bg sticky-top">
                    <div
                      className="ct_custom_badge ms-auto"
                      style={{
                        backgroundColor: isSupplierActive
                          ? "rgb(203, 247, 255)"
                          : "rgba(255, 107, 107, 0.12)",
                        color: isSupplierActive
                          ? "rgb(35, 192, 232)"
                          : "#FF6B6B",
                      }}
                    >
                      <span
                        className="ct_custom_dot"
                        style={{
                          backgroundColor: isSupplierActive
                            ? "rgb(35, 192, 232)"
                            : "#FF6B6B",
                        }}
                      />
                      <small className="ct_ff_ProximaNova_regular">
                        {isSupplierActive ? "ACTIVE" : "INACTIVE"}
                      </small>
                    </div>
                    <div>
                      <div className="ct_img_128">
                        <img
                          src={
                            supplierData?.profile_card?.logo_url ||
                            "assets/img/user-detail-marine-part.jpg"
                          }
                          alt=""
                        />
                      </div>
                      <div className="mt-3 text-center">
                        <h4 className=" mb-0 ct_fs_24 ct_ff_ProximaNova_semibold " style={{wordBreak:"break-word"}}>
                          {supplierData?.profile_card?.display_name || "-"}
                        </h4>
                        <p className="mb-0 ct_text_theme ct_ff_ProximaNova_semibold" style={{wordBreak:"break-word"}}>
                          {supplierData?.profile_card?.sub_title || "-"}
                        </p>
                      </div>
                    </div>
                    <ul className="ct_user_dtl_list">
                      <li>
                        <div className="ct_icon_box">
                          <svg
                            width={16}
                            height={20}
                            viewBox="0 0 16 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M8 10C8.55 10 9.02083 9.80417 9.4125 9.4125C9.80417 9.02083 10 8.55 10 8C10 7.45 9.80417 6.97917 9.4125 6.5875C9.02083 6.19583 8.55 6 8 6C7.45 6 6.97917 6.19583 6.5875 6.5875C6.19583 6.97917 6 7.45 6 8C6 8.55 6.19583 9.02083 6.5875 9.4125C6.97917 9.80417 7.45 10 8 10ZM8 17.35C10.0333 15.4833 11.5417 13.7875 12.525 12.2625C13.5083 10.7375 14 9.38333 14 8.2C14 6.38333 13.4208 4.89583 12.2625 3.7375C11.1042 2.57917 9.68333 2 8 2C6.31667 2 4.89583 2.57917 3.7375 3.7375C2.57917 4.89583 2 6.38333 2 8.2C2 9.38333 2.49167 10.7375 3.475 12.2625C4.45833 13.7875 5.96667 15.4833 8 17.35ZM8 20C5.31667 17.7167 3.3125 15.5958 1.9875 13.6375C0.6625 11.6792 0 9.86667 0 8.2C0 5.7 0.804167 3.70833 2.4125 2.225C4.02083 0.741667 5.88333 0 8 0C10.1167 0 11.9792 0.741667 13.5875 2.225C15.1958 3.70833 16 5.7 16 8.2C16 9.86667 15.3375 11.6792 14.0125 13.6375C12.6875 15.5958 10.6833 17.7167 8 20Z"
                              fill="#013760"
                            />
                          </svg>
                        </div>
                        <div>
                          <small className="ct_fs_12 ct_ff_ProximaNova_semibold ct_text_para">
                            BASE PORT
                          </small>
                          <p className="mb-0 ct_ff_ProximaNova_regular ct_fs_15 ct_text_dark">
                            {supplierData?.profile_card?.base_port || "-"}
                          </p>
                        </div>
                      </li>
                      <li>
                        <div className="ct_icon_box">
                          <svg
                            width={20}
                            height={16}
                            viewBox="0 0 20 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16H2ZM10 9L2 4V14H18V4L10 9ZM10 7L18 2H2L10 7ZM2 4V2V4V14V4Z"
                              fill="#013760"
                            />
                          </svg>
                        </div>
                        <div>
                          <small className="ct_fs_12 ct_ff_ProximaNova_semibold ct_text_para">
                            EMAIL ADDRESS
                          </small>
                          <p className="mb-0 ct_ff_ProximaNova_regular ct_fs_15 ct_text_dark" style={{wordBreak:"break-word"}}>
                            {supplierData?.profile_card?.email || "-"}
                          </p>
                        </div>
                      </li>
                      <li>
                        <div className="ct_icon_box">
                          <svg
                            width={18}
                            height={18}
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M16.95 18C14.8667 18 12.8083 17.5458 10.775 16.6375C8.74167 15.7292 6.89167 14.4417 5.225 12.775C3.55833 11.1083 2.27083 9.25833 1.3625 7.225C0.454167 5.19167 0 3.13333 0 1.05C0 0.75 0.1 0.5 0.3 0.3C0.5 0.1 0.75 0 1.05 0H5.1C5.33333 0 5.54167 0.0791667 5.725 0.2375C5.90833 0.395833 6.01667 0.583333 6.05 0.8L6.7 4.3C6.73333 4.56667 6.725 4.79167 6.675 4.975C6.625 5.15833 6.53333 5.31667 6.4 5.45L3.975 7.9C4.30833 8.51667 4.70417 9.1125 5.1625 9.6875C5.62083 10.2625 6.125 10.8167 6.675 11.35C7.19167 11.8667 7.73333 12.3458 8.3 12.7875C8.86667 13.2292 9.46667 13.6333 10.1 14L12.45 11.65C12.6 11.5 12.7958 11.3875 13.0375 11.3125C13.2792 11.2375 13.5167 11.2167 13.75 11.25L17.2 11.95C17.4333 12.0167 17.625 12.1375 17.775 12.3125C17.925 12.4875 18 12.6833 18 12.9V16.95C18 17.25 17.9 17.5 17.7 17.7C17.5 17.9 17.25 18 16.95 18ZM3.025 6L4.675 4.35L4.25 2H2.025C2.10833 2.68333 2.225 3.35833 2.375 4.025C2.525 4.69167 2.74167 5.35 3.025 6ZM11.975 14.95C12.625 15.2333 13.2875 15.4583 13.9625 15.625C14.6375 15.7917 15.3167 15.9 16 15.95V13.75L13.65 13.275L11.975 14.95Z"
                              fill="#013760"
                            />
                          </svg>
                        </div>
                        <div>
                          <small className="ct_fs_12 ct_ff_ProximaNova_semibold ct_text_para">
                            PRIMARY CONTACT
                          </small>
                          <p className="mb-0 ct_ff_ProximaNova_regular ct_fs_15 ct_text_dark">
                            {supplierData?.profile_card?.phone_number || "-"}
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="col-xl-8 mb-4">
                  <ul
                    className="nav nav-tabs dashboard-tabs "
                    id="myTab"
                    role="tablist"
                  >
                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link active"
                        id="supplier-info-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#supplier-info"
                        type="button"
                        role="tab"
                        aria-controls="supplier-info"
                        aria-selected="true"
                      >
                        Supplier Info
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link"
                        id="supplier-jobs-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#supplier-jobs"
                        type="button"
                        role="tab"
                        aria-controls="supplier-jobs"
                        aria-selected="false"
                      >
                        Supplier Jobs
                      </button>
                    </li>
                    <li className="nav-item" role="presentation">
                      <button
                        className="nav-link"
                        id="supplier-activity-tab"
                        data-bs-toggle="tab"
                        data-bs-target="#supplier-activity"
                        type="button"
                        role="tab"
                        aria-controls="supplier-activity"
                        aria-selected="false"
                      >
                        Supplier Activity
                      </button>
                    </li>
                  </ul>
                  <div className="mt-3">
                    <div className="tab-content border-0" id="myTabContent">
                      <div
                        className="tab-pane fade show active"
                        id="supplier-info"
                        role="tabpanel"
                        aria-labelledby="supplier-info-tab"
                      >
                        <div className="dock_info h-100">
                          <div className="d-flex justify-content-between gap-2 align-items-center mb-3">
                            <h3 className="ct_fs_20 ct_fw_600">
                              Company Profile
                            </h3>
                          </div>
                          <div className="dock_grid">
                            <div className="dock_item">
                              <div>
                                <p className="label">Name</p>
                                <p className="value text-break">
                                  {supInfo?.username || "-"}
                                </p>
                              </div>
                            </div>
                            <div className="dock_item">
                              <div>
                                <p className="label"> mail</p>
                                <p className="value text-break">
                                  {supInfo?.company_email || "-"}
                                </p>
                              </div>
                            </div>
                            <div className="dock_item">
                              <div>
                                <p className="label">JOINED DATE</p>
                                <p className="value text-break">
                                  {pipViewDate(supInfo?.joined_date) || "-"}
                                </p>
                              </div>
                            </div>
                            <div className="dock_item">
                              <div>
                                <p className="label">ABN</p>
                                <p className="value text-break">
                                  {supInfo?.abn || "-"}
                                </p>
                              </div>
                            </div>
                            <div className="dock_item">
                              <div>
                                <p className="label">User Type</p>
                                <p className="value text-break ct_text_theme">
                                  {supInfo?.user_type || "-"}
                                </p>
                              </div>
                            </div>
                            <div className="dock_item">
                              <div>
                                <p className="label">Accounting Software</p>
                                <p className="value text-break">
                                  {supInfo?.accounting_software || "-"}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="ct_mt_34">
                            <p className="ct_text_para ct_fs_16 mb-0 ct_max_height_250 ct_custom_scroll pe-2">
                              {supplierCompanyDetails || "-"}
                            </p>
                          </div>
                          {services.length > 0 && (
                            <div className="ct_mt_34">
                              <h3 className="ct_fs_16 ct_fw_600 text-uppercase mb-4">
                                SERVICES OFFERED
                              </h3>
                              <div className="supp-service-offered">
                                {visibleServices.map((item, index) => (
                                  <p key={index}>{item || "-"}</p>
                                ))}
                                {!showAll && remainingCount > 0 && (
                                  <p className="view-more">
                                    {" "}
                                    +{remainingCount} Specialized
                                  </p>
                                )}
                                {showAll && services.length > MAX_VISIBLE && (
                                  <span
                                    className="ct_specialized_btn"
                                    onClick={() => setShowAll(false)}
                                    style={{ cursor: "pointer" }}
                                  >
                                    Show Less
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          {supplierData?.documents?.length > 0 && (
                            <div className="ct_mt_34">
                              <h3 className="ct_fs_16 ct_fw_600 text-uppercase mb-4">
                                COMPLIANCE &amp; DOCUMENTS
                              </h3>
                              <div className="row">
                                {supplierData?.documents?.map((item, index) => (
                                  <div
                                    className="col-lg-6"
                                    onClick={() => handleView(item?.url)}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <div className="service-info-doc-box">
                                      <div>
                                        <svg
                                          width={48}
                                          height={48}
                                          viewBox="0 0 48 48"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                        >
                                          <rect
                                            width={48}
                                            height={48}
                                            rx={12}
                                            fill="#FEF2F2"
                                          />
                                          <path
                                            d="M20 30H28V28H20V30ZM20 26H28V24H20V26ZM18 34C17.45 34 16.9792 33.8042 16.5875 33.4125C16.1958 33.0208 16 32.55 16 32V16C16 15.45 16.1958 14.9792 16.5875 14.5875C16.9792 14.1958 17.45 14 18 14H26L32 20V32C32 32.55 31.8042 33.0208 31.4125 33.4125C31.0208 33.8042 30.55 34 30 34H18ZM25 21V16H18V32H30V21H25ZM18 16V21V16V21V32V16Z"
                                            fill="#CB0B03"
                                          />
                                        </svg>
                                      </div>
                                      <div>
                                        <h3 className="ct_fs_14 ct_fw_700 ct_text_dark mb-0">
                                          {getFileName(item?.url) || item?.name}
                                        </h3>
                                        <p className="ct_text_para ct_fs-12 mb-0">
                                          {getFileName(item?.url) || item?.name}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                {/* <div className="col-lg-6">
                                  <div className="service-info-doc-box">
                                    <div>
                                      <svg
                                        width={48}
                                        height={48}
                                        viewBox="0 0 48 48"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                      >
                                        <rect
                                          width={48}
                                          height={48}
                                          rx={12}
                                          fill="#EFF6FF"
                                        />
                                        <path
                                          d="M22.95 27.55L28.6 21.9L27.175 20.475L22.95 24.7L20.85 22.6L19.425 24.025L22.95 27.55ZM24 34C21.6833 33.4167 19.7708 32.0875 18.2625 30.0125C16.7542 27.9375 16 25.6333 16 23.1V17L24 14L32 17V23.1C32 25.6333 31.2458 27.9375 29.7375 30.0125C28.2292 32.0875 26.3167 33.4167 24 34ZM24 31.9C25.7333 31.35 27.1667 30.25 28.3 28.6C29.4333 26.95 30 25.1167 30 23.1V18.375L24 16.125L18 18.375V23.1C18 25.1167 18.5667 26.95 19.7 28.6C20.8333 30.25 22.2667 31.35 24 31.9Z"
                                          fill="#23C0E8"
                                        />
                                      </svg>
                                    </div>
                                    <div>
                                      <h3 className="ct_fs_14 ct_fw_700 ct_text_dark mb-0">
                                        Professional Indemnity
                                      </h3>
                                      <p className="ct_text_para ct_fs-12 mb-0">
                                        1.1 MB â€¢ PDF
                                      </p>
                                    </div>
                                  </div>
                                </div> */}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div
                        className="tab-pane fade"
                        id="supplier-jobs"
                        role="tabpanel"
                        aria-labelledby="supplier-jobs-tab"
                      >
                        <div className="ct_supplier_card_bg ct_mb_32">
                          <div className="row ct_mb_55 ct_suplier_job_card">
                            <div className="col-xxl-4 col-xl-6 col-lg-4 col-md-4 mb-4 mb-xxl-0">
                              <div className="ct_white_bg ct_p_24 ct_user_card_new ct_border_radius_12 h-100">
                                <div>
                                  <p className="mb-1 ct_fs_14 ct_text_para ct_ff_ProximaNova_semibold">
                                    TOTAL JOBS
                                  </p>
                                  <h3 className=" ct_fs_30 mb-0 text-dark ct_ff_ProximaNova_bold">
                                    {" "}
                                    {supJobs?.total_jobs || 0}
                                  </h3>
                                </div>
                                <div className="ct_user_icon">
                                  <svg
                                    width={18}
                                    height={20}
                                    viewBox="0 0 18 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4C0 3.45 0.195833 2.97917 0.5875 2.5875C0.979167 2.19583 1.45 2 2 2H6.2C6.41667 1.4 6.77917 0.916667 7.2875 0.55C7.79583 0.183333 8.36667 0 9 0C9.63333 0 10.2042 0.183333 10.7125 0.55C11.2208 0.916667 11.5833 1.4 11.8 2H16C16.55 2 17.0208 2.19583 17.4125 2.5875C17.8042 2.97917 18 3.45 18 4V18C18 18.55 17.8042 19.0208 17.4125 19.4125C17.0208 19.8042 16.55 20 16 20H2ZM2 18H16V4H2V18ZM4 16H11V14H4V16ZM4 12H14V10H4V12ZM4 8H14V6H4V8ZM9 3.25C9.21667 3.25 9.39583 3.17917 9.5375 3.0375C9.67917 2.89583 9.75 2.71667 9.75 2.5C9.75 2.28333 9.67917 2.10417 9.5375 1.9625C9.39583 1.82083 9.21667 1.75 9 1.75C8.78333 1.75 8.60417 1.82083 8.4625 1.9625C8.32083 2.10417 8.25 2.28333 8.25 2.5C8.25 2.71667 8.32083 2.89583 8.4625 3.0375C8.60417 3.17917 8.78333 3.25 9 3.25ZM2 18V4V18Z"
                                      fill="#192333"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div className="col-xxl-4 col-xl-6 col-lg-4 col-md-4 mb-4 mb-xxl-0">
                              <div className="ct_white_bg ct_p_24 ct_user_card_new ct_border_radius_12 h-100">
                                <div>
                                  <p className="mb-1 ct_fs_14 ct_text_para ct_ff_ProximaNova_semibold">
                                    IN PROGRESS
                                  </p>
                                  <h3 className=" ct_fs_30 mb-0 text-dark ct_ff_ProximaNova_bold">
                                    {" "}
                                    {supJobs?.in_progress_jobs || 0}
                                  </h3>
                                </div>
                                <div className="ct_user_icon">
                                  <svg
                                    width={19}
                                    height={21}
                                    viewBox="0 0 19 21"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M14 21C12.6167 21 11.4375 20.5125 10.4625 19.5375C9.4875 18.5625 9 17.3833 9 16C9 14.6167 9.4875 13.4375 10.4625 12.4625C11.4375 11.4875 12.6167 11 14 11C15.3833 11 16.5625 11.4875 17.5375 12.4625C18.5125 13.4375 19 14.6167 19 16C19 17.3833 18.5125 18.5625 17.5375 19.5375C16.5625 20.5125 15.3833 21 14 21ZM15.675 18.375L16.375 17.675L14.5 15.8V13H13.5V16.2L15.675 18.375ZM2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4C0 3.45 0.195833 2.97917 0.5875 2.5875C0.979167 2.19583 1.45 2 2 2H6.175C6.35833 1.41667 6.71667 0.9375 7.25 0.5625C7.78333 0.1875 8.36667 0 9 0C9.66667 0 10.2625 0.1875 10.7875 0.5625C11.3125 0.9375 11.6667 1.41667 11.85 2H16C16.55 2 17.0208 2.19583 17.4125 2.5875C17.8042 2.97917 18 3.45 18 4V10.25C17.7 10.0333 17.3833 9.85 17.05 9.7C16.7167 9.55 16.3667 9.41667 16 9.3V4H14V7H4V4H2V18H7.3C7.41667 18.3667 7.55 18.7167 7.7 19.05C7.85 19.3833 8.03333 19.7 8.25 20H2ZM9 4C9.28333 4 9.52083 3.90417 9.7125 3.7125C9.90417 3.52083 10 3.28333 10 3C10 2.71667 9.90417 2.47917 9.7125 2.2875C9.52083 2.09583 9.28333 2 9 2C8.71667 2 8.47917 2.09583 8.2875 2.2875C8.09583 2.47917 8 2.71667 8 3C8 3.28333 8.09583 3.52083 8.2875 3.7125C8.47917 3.90417 8.71667 4 9 4Z"
                                      fill="#596575"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                            <div className="col-xxl-4 col-xl-6 col-lg-4 col-md-4 mb-4 mb-xxl-0">
                              <div className="ct_white_bg ct_p_24 ct_user_card_new ct_border_radius_12 h-100">
                                <div>
                                  <p className="mb-1 ct_fs_14 ct_text_para ct_ff_ProximaNova_semibold">
                                    COMPLETED
                                  </p>
                                  <h3 className=" ct_fs_30 mb-0 text-dark ct_ff_ProximaNova_bold">
                                    {" "}
                                    {supJobs?.completed_jobs || 0}
                                  </h3>
                                </div>
                                <div className="ct_user_icon ct_user_icon_last">
                                  <svg
                                    width={22}
                                    height={21}
                                    viewBox="0 0 22 21"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M7.6 21L5.7 17.8L2.1 17L2.45 13.3L0 10.5L2.45 7.7L2.1 4L5.7 3.2L7.6 0L11 1.45L14.4 0L16.3 3.2L19.9 4L19.55 7.7L22 10.5L19.55 13.3L19.9 17L16.3 17.8L14.4 21L11 19.55L7.6 21ZM8.45 18.45L11 17.35L13.6 18.45L15 16.05L17.75 15.4L17.5 12.6L19.35 10.5L17.5 8.35L17.75 5.55L15 4.95L13.55 2.55L11 3.65L8.4 2.55L7 4.95L4.25 5.55L4.5 8.35L2.65 10.5L4.5 12.6L4.25 15.45L7 16.05L8.45 18.45ZM9.95 14.05L15.6 8.4L14.2 6.95L9.95 11.2L7.8 9.1L6.4 10.5L9.95 14.05Z"
                                      fill="#1FB3AF"
                                    />
                                  </svg>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="ct_white_bg p-4 ct_border_radius_12">
                          <h3 className="ct_fs_20 ct_fw_600 mb-3 ct_ff_ProximaNova_semibold ">
                            Job History{" "}
                          </h3>
                          <div className="custom_table_wrapper mt-3">
                            <div className="table-responsive  ct_custom_table">
                              <table className="table">
                                <thead>
                                  <tr>
                                    <th>S.NO</th>
                                    <th>JOB NAME</th>
                                    <th>BOAT REG.</th>
                                    <th>DATE SCHEDULED</th>
                                    <th>STATUS</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {displayUsers?.length != 0 ? (
                                    displayUsers?.map((item, i) => (
                                      <tr key={item?.id || item?.jobId || item?.taskId || i}>
                                        <td className="ct_text_para">
                                          {jobCurrentPage * jobRowsPerPage + i + 1}
                                        </td>
                                        <td className="ct_text_theme ct_ff_ProximaNova_bold">
                                          {item?.jobName || "-"}
                                        </td>
                                        <td className="ct_text_para">
                                          {item?.boatReg || "-"}
                                        </td>
                                        <td className="ct_text_para">
                                          {item?.scheduledDate || "-"}
                                        </td>
                                        <StatusCol
                                          status={item?.status}
                                          type={"jobHis"}
                                        />
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan={6}>
                                        <h3 className="mt-3 mb-0 ct_fs_18 text-center">
                                          No job history found
                                        </h3>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      {supplierJobHistory?.length > 0 && (
                          <div className="d-flex align-items-center flex-wrap justify-content-between gap-3 mt-3 mb-3">
                            <PaginationDropdown
                              onChange={(val) => {
                                setJobRowsPerPage(Number(val));
                                setJobCurrentPage(0);
                              }}
                            />
                            <ReactPagination
                              pageCount={Math.ceil(
                                supplierJobHistory?.length / jobRowsPerPage,
                              )}
                              onPageChange={handlePageClick}
                              currentPage={jobCurrentPage}
                            />
                          </div>
                        )}
                      </div>
                      <div
                        className="tab-pane fade"
                        id="supplier-activity"
                        role="tabpanel"
                        aria-labelledby="supplier-activity-tab"
                      >
                        {/* <div
                          className="ct_white_bg p-3 ct_border_radius_12 ct_mb_32 d-flex align-items-center justify-content-between flex-wrap gap-3"
                          style={{
                            border: liveStatus?.currently_working
                              ? "1px solid rgba(29,158,117,0.3)"
                              : "1px solid rgba(0,0,0,0.06)",
                            background: liveStatus?.currently_working
                              ? "rgba(29,158,117,0.05)"
                              : "#fff",
                          }}
                        >
                          <div className="d-flex align-items-center gap-3">
                            <span
                              style={{
                                width: 12,
                                height: 12,
                                borderRadius: "50%",
                                backgroundColor: liveStatus?.currently_working
                                  ? "#1D9E75"
                                  : "#888780",
                                display: "inline-block",
                                flexShrink: 0,
                                boxShadow: liveStatus?.currently_working
                                  ? "0 0 0 4px rgba(29,158,117,0.2)"
                                  : "none",
                              }}
                            />
                            <div>
                              <p className="mb-0 ct_fs_15 ct_fw_600 ct_text_dark">
                                {liveStatus?.currently_working
                                  ? "Currently Working"
                                  : "Not Currently Working"}
                              </p>
                              <p className="mb-0 ct_fs_12 ct_text_para">
                                Active Jobs:{" "}
                                <strong>{liveStatus?.active_jobs ?? 0}</strong>
                                {liveStatus?.last_completed_job && (
                                  <span className="ms-3">
                                    Last Completed Job:{" "}
                                    <strong>
                                      #{liveStatus?.last_completed_job}
                                    </strong>
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <StatusBadge
                            value={
                              liveStatus?.currently_working
                                ? "Online"
                                : "Offline"
                            }
                            color={
                              liveStatus?.currently_working ? "green" : "gray"
                            }
                          />
                        </div> */}

                        {renderActivityCards(
                          "Performance Summary",
                          performanceSummaryRows,
                        )}
                         {renderActivityTable(
                          "Supplier Engagement",
                          engagementRows,
                        )}
                        <SupplierPerformanceChart perfSummary={perfSummary}  supEngagement={supEngagement} workActivity={workActivity}/> 
                        {renderSupplierBoatsTable()}
                        {/* {renderActivityTable("Work Activity", workActivityRows)} */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeleteModal
        modalId="supplier_status_modal"
        heading={statusModalHeading}
        value={statusModalText}
        confirmText={statusButtonText}
        handleDelete={handleStatusToggle}
      />
    </div>
  );
};

export default SupplierDetail;
